import type { Lead } from '../types';
import { STATUS_COLOR, STATUS_LABEL } from '../types';

const TIER_META: Record<1 | 2 | 3, { label: string; badge: string; dot: string }> = {
  1: { label: 'HOT', badge: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500' },
  2: { label: 'WARM', badge: 'bg-amber-100 text-amber-800 border-amber-200', dot: 'bg-amber-400' },
  3: { label: 'COLD', badge: 'bg-slate-100 text-slate-500 border-slate-200', dot: 'bg-slate-400' },
};

const SOURCE_META: Record<Lead['source'], { label: string; icon: string; badge: string }> = {
  whatsapp: { 
    label: 'WhatsApp', 
    icon: '📱', 
    badge: 'bg-green-100 text-green-800 border-green-200 font-medium' 
  },
  meta_form: { 
    label: 'Meta', 
    icon: '📘', 
    badge: 'bg-blue-100 text-blue-800 border-blue-200 font-medium' 
  },
  call: { 
    label: 'Call', 
    icon: '📞', 
    badge: 'bg-purple-100 text-purple-800 border-purple-200 font-medium' 
  },
  neodove: {
    label: 'NeoDove',
    icon: '☎️',
    badge: 'bg-cyan-100 text-cyan-800 border-cyan-200 font-medium'
  },
};

function fmtTime(iso: string) {
  const d = new Date(iso.replace(' ', 'T') + 'Z');
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString([], { day: 'numeric', month: 'short' });
}

export default function LeadCard({
  lead,
  onSelect,
  showStatus = false,
  me,
  selected = false,
  action,
}: {
  lead: Lead;
  onSelect: (id: string) => void;
  showStatus?: boolean;
  me?: string;
  selected?: boolean;
  action?: {
    label: string;
    onClick: (id: string) => void;
    tone?: 'primary' | 'secondary';
    disabled?: boolean;
  };
}) {
  const meta = TIER_META[lead.tier_score];
  const sourceMeta = SOURCE_META[lead.source];
  const mine = me && lead.assigned_to === me;
  const assignedLabel = lead.assigned_to
    ? mine
      ? 'You'
      : lead.assigned_to.split('@')[0]
    : null;
  const lastAssignedLabel = lead.last_assigned_to
    ? lead.last_assigned_to === lead.assigned_to
      ? null
      : lead.last_assigned_to.split('@')[0]
    : null;

  return (
    <div
      className={`block w-full overflow-hidden rounded-2xl border bg-white text-left shadow-sm active:scale-[0.995] ${
        selected ? 'border-wechat-accent ring-2 ring-wechat-accent/20' : 'border-slate-200'
      }`}
    >
      <button type="button" onClick={() => onSelect(lead.id)} className="block w-full text-left">
        <div className="flex items-start gap-3 px-4 pt-3">
          <div className={`mt-1 h-2.5 w-2.5 flex-none rounded-full ${meta.dot}`} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                {sourceMeta && (
                  <span className={`rounded-full border px-2 py-1 text-[10px] font-bold ${sourceMeta.badge}`}>
                    {sourceMeta.icon} {sourceMeta.label}
                  </span>
                )}
                <h2 className="truncate text-base font-semibold text-slate-900">{lead.name}</h2>
              </div>
              <span
                className={`flex-none rounded-full border px-2 py-0.5 text-[10px] font-bold tracking-wider ${meta.badge}`}
              >
                {meta.label}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-slate-500">
              <span>{lead.phone}</span>
              <span>·</span>
              <span>{fmtTime(lead.created_at)}</span>
              {assignedLabel && (
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                    mine ? 'bg-emerald-100 text-emerald-700' : 'bg-indigo-100 text-indigo-700'
                  }`}
                >
                  {mine ? '👤 You' : `→ ${assignedLabel}`}
                </span>
              )}
              {!assignedLabel && lastAssignedLabel && (
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                  Previously: {lastAssignedLabel}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="mx-4 mt-3 rounded-2xl rounded-tl-sm bg-wechat-bubble px-3 py-2 text-sm text-slate-900">
          {lead.opening_line}
        </div>

        <div className="flex items-center justify-between px-4 pb-3 pt-2 text-xs">
          <span className="italic text-slate-500 line-clamp-1">{lead.persuasion_tip}</span>
          {showStatus && (
            <span
              className={`ml-2 flex-none rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS_COLOR[lead.status]}`}
            >
              {STATUS_LABEL[lead.status]}
            </span>
          )}
        </div>
      </button>

      {action && (
        <div className="px-4 pb-4">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              action.onClick(lead.id);
            }}
            disabled={action.disabled}
            className={`w-full rounded-xl px-3 py-2 text-sm font-semibold ${
              action.tone === 'secondary'
                ? 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                : 'bg-emerald-600 text-white hover:bg-emerald-700'
            } disabled:cursor-not-allowed disabled:opacity-60`}
          >
            {action.label}
          </button>
        </div>
      )}
    </div>
  );
}
